import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Thermometer,
  Wind,
  Sun,
  Droplet,
  Fan,
  Home,
  Square,
  Box,
  Check,
  ArrowRight,
  Award,
  TrendingDown,
  Leaf,
  Shield,
} from 'lucide-react';
import api from '@/api/api';

const HomePage = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    loadServices();
  }, []);

  const loadServices = async () => {
    // Skip API calls during pre-rendering (react-snap)
    if (navigator.userAgent === 'ReactSnap') {
      return;
    }

    try {
      const data = await api.getServices();
      setServices(data);
    } catch (error) {
      // Only log in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error loading services:', error);
      }
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      thermometer: Thermometer,
      wind: Wind,
      sun: Sun,
      droplet: Droplet,
      fan: Fan,
      home: Home,
      square: Square,
      wall: Box,
    };
    return icons[iconName] || Home;
  };

  return (
    <div className="HomePage">
      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center py-24 md:py-32"
        style={{
          backgroundImage: 'url(/images/pac-2.webp)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D9A68]/95 to-[#2F5BA0]/90"></div>
        <div className="relative container mx-auto px-4" data-testid="hero-section">
          <div className="max-w-3xl text-white">
            <div className="mb-6 flex gap-3 flex-wrap">
              <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold">
                ✅ Certifié QualiPAC
              </span>
              <span className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold">
                ✅ Certifié Qualibat
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 animate-fade-in">
              Spécialiste de la Rénovation Énergétique
            </h1>
            <p className="text-xl md:text-2xl mb-8 animate-slide-in-left">
              Pompes à chaleur, isolation, ventilation - Bénéficiez des aides de l'État jusqu'à <span className="font-bold text-[#E6A835]">15 990€</span>
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-slide-in-right">
              <Link
                to="/test-eligibilite"
                className="bg-[#E6A835] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#d49520] transition-all hover:shadow-xl inline-flex items-center justify-center gap-2"
                data-testid="hero-cta-button"
              >
                Testez votre éligibilité <ArrowRight />
              </Link>
              <a
                href="tel:0188617508"
                className="bg-white text-[#2D9A68] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:shadow-xl inline-flex items-center justify-center gap-2"
                data-testid="hero-call-button"
              >
                01 88 61 75 08
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Bannière Aides PAC Septembre 2025 */}
      <section className="py-12 bg-gradient-to-r from-[#E6A835] via-[#d49520] to-[#E6A835]">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center text-white">
            <div className="inline-block bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm font-bold mb-4 animate-pulse">
              🔥 NOUVEAU - Arrêté Septembre 2025
            </div>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Aides Exceptionnelles pour les Pompes à Chaleur
            </h2>
            <p className="text-xl md:text-2xl mb-6 font-semibold">
              Jusqu'à <span className="text-5xl font-black">15 990€</span> d'aides cumulées
            </p>
            <p className="text-lg mb-8 max-w-3xl mx-auto">
              Profitez des nouvelles dispositions de l'arrêté de septembre 2025 : aides renforcées pour remplacer votre ancien chauffage par une pompe à chaleur performante
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="text-4xl font-bold mb-2">5 000€</div>
                <div className="text-sm">MaPrimeRénov'</div>
                <div className="text-xs mt-1 opacity-90">PAC Air-Eau</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="text-4xl font-bold mb-2">10 990€</div>
                <div className="text-sm">Prime CEE</div>
                <div className="text-xs mt-1 opacity-90">Certificats Énergie</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <div className="text-4xl font-bold mb-2">0€</div>
                <div className="text-sm">Reste à charge possible</div>
                <div className="text-xs mt-1 opacity-90">Selon revenus</div>
              </div>
            </div>
            <Link
              to="/test-eligibilite"
              className="inline-flex items-center gap-2 bg-white text-[#E6A835] px-10 py-5 rounded-lg font-bold text-xl hover:bg-gray-100 transition-all hover:shadow-2xl shadow-xl"
              data-testid="pac-aids-cta-button"
            >
              Je calcule mes aides <ArrowRight size={24} />
            </Link>
          </div>
        </div>
      </section>

      {/* Pourquoi nous choisir */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Pourquoi choisir Climatpac 81 ?
            </h2>
            <div className="section-decorator"></div>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center p-6 rounded-lg hover:shadow-lg transition-all" data-testid="benefit-card">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2D9A68]/10 text-[#2D9A68] rounded-full mb-4">
                <TrendingDown size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Économies</h3>
              <p className="text-gray-600">Réduisez votre facture jusqu'à -70%</p>
            </div>
            <div className="text-center p-6 rounded-lg hover:shadow-lg transition-all" data-testid="benefit-card">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2D9A68]/10 text-[#2D9A68] rounded-full mb-4">
                <Leaf size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Écologique</h3>
              <p className="text-gray-600">Énergie renouvelable et durable</p>
            </div>
            <div className="text-center p-6 rounded-lg hover:shadow-lg transition-all" data-testid="benefit-card">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#2F5BA0]/10 text-[#2F5BA0] rounded-full mb-4">
                <Thermometer size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Confort</h3>
              <p className="text-gray-600">Chauffage + eau chaude toute l'année</p>
            </div>
            <div className="text-center p-6 rounded-lg hover:shadow-lg transition-all" data-testid="benefit-card">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E6A835]/10 text-[#E6A835] rounded-full mb-4">
                <Award size={32} />
              </div>
              <h3 className="text-xl font-bold mb-2 text-gray-900">Certifié</h3>
              <p className="text-gray-600">QualiPAC & Qualibat</p>
            </div>
          </div>
        </div>
      </section>

      {/* Nos Services */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Nos Services</h2>
            <div className="section-decorator"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Des solutions complètes pour améliorer le confort et la performance énergétique de votre logement
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service) => {
              const IconComponent = getIcon(service.icon);
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.slug}`}
                  className="service-card bg-white p-6 rounded-xl shadow-md hover:shadow-2xl transition-all"
                  data-testid="service-card"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-br from-[#2D9A68] to-[#2F5BA0] text-white rounded-lg mb-4">
                    <IconComponent size={28} />
                  </div>
                  <h3 className="text-lg font-bold mb-2 text-gray-900">{service.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{service.description}</p>
                  <span className="text-[#2D9A68] font-semibold inline-flex items-center gap-1 text-sm">
                    En savoir plus <ArrowRight size={16} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Aides financières - Arrêté Septembre 2025 */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <div className="inline-block bg-[#E6A835] text-white px-6 py-2 rounded-full text-sm font-bold mb-4">
                Arrêté de Septembre 2025
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Nouvelles Aides Renforcées pour les Pompes à Chaleur
              </h2>
              <div className="section-decorator"></div>
              <p className="text-gray-600 mt-4 max-w-3xl mx-auto">
                L'arrêté de septembre 2025 renforce les dispositifs d'aides pour accélérer la transition énergétique. 
                Profitez de conditions exceptionnelles pour installer votre pompe à chaleur.
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-[#2D9A68] to-[#237852] text-white p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4">MaPrimeRénov'</h3>
                <p className="mb-6 text-white/90">
                  Aide de l'État pour financer vos travaux de rénovation énergétique
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 flex-shrink-0" size={20} />
                    <span>Jusqu'à <strong>5 000€</strong> pour une PAC Air-Eau</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 flex-shrink-0" size={20} />
                    <span>Jusqu'à <strong>4 000€</strong> pour un ballon solaire</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 flex-shrink-0" size={20} />
                    <span>Jusqu'à <strong>25€/m²</strong> pour l'isolation</span>
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-[#2F5BA0] to-[#1e3a66] text-white p-8 rounded-xl shadow-lg">
                <h3 className="text-2xl font-bold mb-4">Certificats CEE</h3>
                <p className="mb-6 text-white/90">
                  Prime énergie accessible sans conditions de revenus
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 flex-shrink-0" size={20} />
                    <span>Jusqu'à <strong>9 158€</strong> pour une PAC Air-Eau</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 flex-shrink-0" size={20} />
                    <span>Montant variable selon les travaux</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="mt-1 flex-shrink-0" size={20} />
                    <span>Cumul possible avec MaPrimeRénov'</span>
                  </li>
                </ul>
              </div>
            </div>
            {/* Nouveautés Arrêté Sept 2025 */}
            <div className="mt-12 bg-gradient-to-br from-[#2D9A68]/10 via-[#2F5BA0]/10 to-[#E6A835]/10 rounded-2xl p-8 border-2 border-[#E6A835]">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                ⚡ Nouveautés de l'Arrêté de Septembre 2025
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#2D9A68] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    1
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Montants augmentés</h4>
                    <p className="text-gray-600 text-sm">
                      Les plafonds de MaPrimeRénov' ont été revus à la hausse pour les ménages modestes et très modestes
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#2F5BA0] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Cumul facilité</h4>
                    <p className="text-gray-600 text-sm">
                      Cumul automatique MaPrimeRénov' + CEE + aides locales pour maximiser votre financement
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#E6A835] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Bonus sortie fioul/gaz</h4>
                    <p className="text-gray-600 text-sm">
                      Prime supplémentaire de 1 000€ pour le remplacement d'une chaudière fioul ou gaz par une PAC
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-[#2D9A68] text-white rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                    4
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Démarches simplifiées</h4>
                    <p className="text-gray-600 text-sm">
                      Dossier unique géré par nos experts RGE, versement rapide des aides sous 2 semaines
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center mt-8">
              <Link
                to="/test-eligibilite"
                className="inline-flex items-center gap-2 bg-[#E6A835] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#d49520] transition-all hover:shadow-xl"
                data-testid="aids-cta-button"
              >
                Calculez vos aides <ArrowRight />
              </Link>
              <p className="text-sm text-gray-500 mt-4">
                Calcul instantané selon l'arrêté de septembre 2025
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Processus en 3 étapes */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Votre installation en 3 étapes simples
            </h2>
            <div className="section-decorator"></div>
          </div>
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center" data-testid="process-step">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#2D9A68] to-[#2F5BA0] text-white rounded-full text-2xl font-bold mb-4">
                  1
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Vérifiez votre éligibilité</h3>
                <p className="text-gray-600">En 2 minutes, découvrez le montant de vos aides</p>
              </div>
              <div className="text-center" data-testid="process-step">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#2D9A68] to-[#2F5BA0] text-white rounded-full text-2xl font-bold mb-4">
                  2
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Visite technique gratuite</h3>
                <p className="text-gray-600">Un expert se déplace chez vous sous 48h</p>
              </div>
              <div className="text-center" data-testid="process-step">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#2D9A68] to-[#2F5BA0] text-white rounded-full text-2xl font-bold mb-4">
                  3
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Installation rapide</h3>
                <p className="text-gray-600">Travaux réalisés par nos artisans RGE</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Nos Certifications</h2>
            <div className="section-decorator"></div>
            <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
              Des labels reconnus qui garantissent la qualité de nos installations et vous permettent de bénéficier des aides
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center p-8 bg-white rounded-xl shadow-lg border-2 border-gray-100 hover:border-[#2F5BA0] transition-all" data-testid="certification-badge">
              <div className="flex items-center justify-center mb-6 h-28 bg-gray-50 rounded-lg p-4">
                <img 
                  src="/images/qualibat-logo-small.webp" 
                  alt="Qualibat RGE" 
                  className="h-24 w-auto"
                />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Qualibat RGE</h3>
              <p className="text-gray-600 text-sm">Label de fiabilité pour la qualité des travaux dans le bâtiment</p>
            </div>
            <div className="text-center p-8 bg-white rounded-xl shadow-lg border-2 border-gray-100 hover:border-[#2D9A68] transition-all" data-testid="certification-badge">
              <div className="flex items-center justify-center mb-6 h-28 bg-gray-50 rounded-lg p-4">
                <img 
                  src="/images/qualipac-logo-small.webp" 
                  alt="QualiPAC RGE" 
                  className="h-24 w-auto"
                />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">QualiPAC RGE</h3>
              <p className="text-gray-600 text-sm">Qualification pompes à chaleur - Reconnu Garant de l'Environnement</p>
            </div>
            <div className="text-center p-8 bg-white rounded-xl shadow-lg border-2 border-gray-100 hover:border-[#2D9A68] transition-all" data-testid="certification-badge">
              <div className="flex items-center justify-center mb-6 h-28 bg-gray-50 rounded-lg p-4">
                <img 
                  src="/images/ventilation-rge-small.webp" 
                  alt="Ventilation+ RGE" 
                  className="h-24 w-auto"
                />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Ventilation+ RGE</h3>
              <p className="text-gray-600 text-sm">Qualification ventilation mécanique contrôlée double flux</p>
            </div>
            <div className="text-center p-8 bg-white rounded-xl shadow-lg border-2 border-gray-100 hover:border-[#E6A835] transition-all" data-testid="certification-badge">
              <div className="flex items-center justify-center mb-6 h-28 bg-gray-50 rounded-lg p-4">
                <img 
                  src="/images/qualisol-rge-small.webp" 
                  alt="Qualisol RGE" 
                  className="h-24 w-auto"
                />
              </div>
              <h3 className="text-xl font-bold mb-3 text-gray-900">Qualisol RGE</h3>
              <p className="text-gray-600 text-sm">Qualification chauffe-eau solaire individuel</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 bg-gradient-to-r from-[#2D9A68] to-[#2F5BA0] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Prêt à réduire vos factures ?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Testez votre éligibilité aux aides de l'État en 2 minutes et découvrez combien vous pouvez économiser
          </p>
          <Link
            to="/test-eligibilite"
            className="inline-flex items-center gap-2 bg-[#E6A835] text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-[#d49520] transition-all hover:shadow-xl"
            data-testid="final-cta-button"
          >
            Je teste mon éligibilité <ArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;